import pegmanIcon from '@Resources/assets/symbology/pegman.png';
import { hexToRgb } from '@Utils/helpers/symbologyHelpers';
import { Layer as DeckGlLayer } from '@deck.gl/core/typed';
import { GoogleMapsOverlay as DeckOverlay } from '@deck.gl/google-maps/typed';
import { GeoJsonLayer, IconLayer, TextLayer } from '@deck.gl/layers/typed';
import { Loader } from '@googlemaps/js-api-loader';
import _isNil from 'lodash/isNil';
import React from 'react';
import { createRoot } from 'react-dom/client';
import { IAnalysisEntity, IQcPole } from '@Data/qualityControl/Interfaces';
import Symbology from '@Views/QualityControl/styles/symbology.scss';
import { parseClientId } from '../MapLibre/Utils';
import Styles from './GoogleMaps.scss';
import { createFovPolygon, getGoogleLatLngBoundsFromFeatures } from './Helpers';
import { IStreetViewPosition } from './StreetViewComponent';
import ClusterLayer from './components/ClusterLayer';
import { LayerControl } from './components/LayerControl';
import { LayerToggle } from './components/LayerToggle';

const INITIAL_MAP_ZOOM_LEVEL = 18;
export const CLUSTER_LEVEL = 16;
export const LABEL_LEVEL = 18;

const backPoleRgb = hexToRgb(Symbology.backPole);
const centrePoleRgb = hexToRgb(Symbology.centerPole);
const forePoleRgb = hexToRgb(Symbology.forePole);

let didBaseMapInit = false;
let isHovering = false;
let hasLoadedControls = false;
const mapOptions: google.maps.MapOptions = {
    zoom: INITIAL_MAP_ZOOM_LEVEL,
    clickableIcons: false,
    fullscreenControl: false,
    keyboardShortcuts: false,
    streetViewControl: false,
    mapTypeControl: false,
    tilt: 0,
    rotateControl: false,
    zoomControl: false,
};

interface IGoogleMapComponentProps {
    id?: string;
    onClick: (feature: any) => void;
    streetViewPosition?: IStreetViewPosition;
    streetViewHeading?: number;
    poles: Array<any>;
    spans?: Array<any>;
    selectedPole: IAnalysisEntity;
    selectedBackPole: IQcPole;
    selectedForePole: IQcPole;
    initialCoordinates: number[];
    poleIconSymbology?: (feature: any) => any;
    spanSymbology?: (feature: any) => [number, number, number, number];
    poleIconUpdateArray?: any;
    visibleMidSpanPhotoLocations?: Array<any>;
    midSpanPhotoLocationIconSymbology?: (isFore: boolean) => any;
}

interface IGoogleMapComponentState {
    zoomLevel: number;
}

export class GoogleMapComponent extends React.Component<
    IGoogleMapComponentProps,
    IGoogleMapComponentState
> {
    id: string;
    topRightControlId: string;
    map: google.maps.Map;
    overlay: DeckOverlay;
    constructor(props: IGoogleMapComponentProps) {
        super(props);
        this.state = {
            zoomLevel: INITIAL_MAP_ZOOM_LEVEL,
        };

        this.id = props.id ?? 'googleMapContainer';
        this.topRightControlId = `${this.id}-topRightContainer`;

        this.loadDeckOverlay = this.loadDeckOverlay.bind(this);
        this.handleClick = this.handleClick.bind(this);
        this.loadLayerSelector = this.loadLayerSelector.bind(this);
        this.panToBounds = this.panToBounds.bind(this);
        this.getSpanSymbology = this.getSpanSymbology.bind(this);
        this.getMidSpanPhotoLocationSymbology =
            this.getMidSpanPhotoLocationSymbology.bind(this);
    }

    componentWillUnmount() {
        didBaseMapInit = false;
        hasLoadedControls = false;
    }

    handleClick(info) {
        this.props.onClick(info.object);
    }

    loadLayerSelector() {
        const rootHtml = document.getElementById(this.topRightControlId);
        if (_isNil(rootHtml)) {
            setTimeout(this.loadLayerSelector, 100);
        } else if (!hasLoadedControls) {
            hasLoadedControls = true;
            const rootRenderer = createRoot(rootHtml);
            rootRenderer.render(
                <LayerToggle
                    onToggle={(newMapType: string) =>
                        this.map.setMapTypeId(newMapType)
                    }
                    initialLayer={this.map.getMapTypeId()}
                />
            );
        }
    }

    loadDeckOverlay() {
        if (this.map) {
            this.overlay = new DeckOverlay({
                getCursor: () => {
                    this.map.setOptions({
                        draggableCursor: isHovering ? 'pointer' : '',
                    });
                    return '';
                },
            });
            this.overlay.setMap(this.map);
        }
    }

    panToBounds(data) {
        const foundObjects = this.props.poles.filter((d) => {
            const index = data.objects.findIndex(
                (o) => o.entity_version_id === d.properties.entity_version_id
            );
            return index > -1;
        });
        const bbox = getGoogleLatLngBoundsFromFeatures(foundObjects);
        this.map.fitBounds(bbox);
    }

    setCenter(longitude: number, latitude: number) {
        if (longitude && latitude && this.map) {
            this.map.panTo({ lat: latitude, lng: longitude });
        }
    }

    getPoleIcon(pole: any) {
        return this.props.poleIconSymbology(pole);
    }

    getSpanSymbology() {
        return new GeoJsonLayer({
            id: 'spansLayer',
            data: this.props.spans,
            getLineWidth: 2,
            lineWidthUnits: 'pixels',
            getLineColor: _isNil(this.props.spanSymbology)
                ? [255, 125, 0, 100]
                : (f) => this.props.spanSymbology(f),
            visible: this.state.zoomLevel >= CLUSTER_LEVEL,
            updateTriggers: {
                visible: [this.state.zoomLevel],
            },
        });
    }

    getMidSpanPhotoLocationSymbology() {
        return new IconLayer({
            id: 'mid-span-photo-location-layer',
            data: this.props.visibleMidSpanPhotoLocations,
            getIcon: (pole) => {
                return this.props.midSpanPhotoLocationIconSymbology(pole);
            },
            getPosition: (d) => [d.lng, d.lat],
            getSize: 15,
            visible: this.state.zoomLevel >= CLUSTER_LEVEL,
            updateTriggers: {
                visible: [this.state.zoomLevel],
                getIcon: this.props.visibleMidSpanPhotoLocations,
            },
        });
    }

    render() {
        if (!didBaseMapInit) {
            const loader = new Loader({
                apiKey: process.env.GOOGLE_API_KEY,
            });

            loader.importLibrary('maps').then(({ Map }) => {
                didBaseMapInit = true;
                this.map = new Map(document.getElementById(this.id), {
                    ...mapOptions,
                    center: {
                        lng: this.props.initialCoordinates[0],
                        lat: this.props.initialCoordinates[1],
                    },
                });
                this.map.addListener('zoom_changed', () => {
                    this.setState({ zoomLevel: this.map.getZoom() });
                });
                this.loadDeckOverlay();

                const topRightControlDiv = document.createElement('div');
                topRightControlDiv.id = this.topRightControlId;
                this.map.controls[google.maps.ControlPosition.TOP_RIGHT].push(
                    topRightControlDiv
                );

                google.maps.event.addListenerOnce(
                    this.map,
                    'tilesloaded',
                    this.loadLayerSelector
                );
            });
        }

        const selectedPole = this.props.poles.find(
            (p) =>
                p.properties.entity_version_id ===
                this.props.selectedPole.entity_version_id
        );

        const selectedBackPole = this.props.poles.find(
            (p) =>
                p.properties.entity_version_id ===
                this.props.selectedBackPole.entityVersionId
        );

        const selectedForePole = this.props.poles.find(
            (p) =>
                p.properties.entity_version_id ===
                this.props.selectedForePole.entityVersionId
        );

        if (!_isNil(this.overlay)) {
            const layers: Array<DeckGlLayer> = [
                new IconLayer({
                    id: 'poles-layer',
                    data: this.props.poles,
                    getIcon: (pole) => {
                        return this.props.poleIconSymbology(pole);
                    },
                    getPosition: (d) => d.geometry.coordinates,
                    getSize: 15,
                    visible: this.state.zoomLevel >= CLUSTER_LEVEL,
                    updateTriggers: {
                        visible: [this.state.zoomLevel],
                        getIcon: this.props.poleIconUpdateArray,
                    },
                    onClick: (info) => this.handleClick(info),
                    onHover: ({ object }) => (isHovering = Boolean(object)),
                    pickable: true,
                }),
                new ClusterLayer({
                    id: 'clusterLayer',
                    data: this.props.poles,
                    //@ts-ignore
                    getPosition: (d) => d.geometry.coordinates,
                    pickable: true,
                    sizeScale: 40,
                    onClick: (d) => this.panToBounds(d),
                    visible: this.state.zoomLevel < CLUSTER_LEVEL,
                    updateTriggers: {
                        visible: [this.state.zoomLevel],
                    },
                }),
                new GeoJsonLayer({
                    id: 'selectedBorder',
                    data: selectedPole,
                    pointType: 'circle',
                    getPointRadius: 12,
                    pointRadiusUnits: 'pixels',
                    getFillColor: [0, 0, 0, 0],
                    getLineColor: centrePoleRgb,
                    getLineWidth: 3,
                    lineWidthUnits: 'pixels',
                    visible: this.state.zoomLevel >= CLUSTER_LEVEL,
                    updateTriggers: {
                        visible: [this.state.zoomLevel],
                    },
                }),
                new GeoJsonLayer({
                    id: 'selectedBackpoleBorder',
                    data: selectedBackPole,
                    pointType: 'circle',
                    getPointRadius: 12,
                    pointRadiusUnits: 'pixels',
                    getFillColor: [0, 0, 0, 0],
                    getLineColor: backPoleRgb ?? [255, 0, 0, 255],
                    getLineWidth: 3,
                    lineWidthUnits: 'pixels',
                    visible: this.state.zoomLevel >= CLUSTER_LEVEL,
                    updateTriggers: {
                        visible: [this.state.zoomLevel],
                    },
                }),
                new GeoJsonLayer({
                    id: 'selectedForepoleBorder',
                    data: selectedForePole,
                    pointType: 'circle',
                    getPointRadius: 12,
                    pointRadiusUnits: 'pixels',
                    getFillColor: [0, 0, 0, 0],
                    getLineColor: forePoleRgb,
                    getLineWidth: 3,
                    lineWidthUnits: 'pixels',
                    visible: this.state.zoomLevel >= CLUSTER_LEVEL,
                    updateTriggers: {
                        visible: [this.state.zoomLevel],
                    },
                }),
                new TextLayer({
                    id: 'poleIdsLayer',
                    data: this.props.poles,
                    getPosition: (d) => d.geometry.coordinates,
                    getText: (d) => parseClientId(d.properties.client_id),
                    getSize: 14,
                    background: true,
                    backgroundPadding: [2, 2],
                    getPixelOffset: [0, -25],
                    visible: this.state.zoomLevel >= LABEL_LEVEL,
                    updateTriggers: {
                        visible: [this.state.zoomLevel],
                    },
                }),
            ];

            if (!_isNil(this.props.spans)) {
                layers.push(this.getSpanSymbology());
            }

            if (
                !_isNil(this.props.visibleMidSpanPhotoLocations) &&
                !_isNil(this.props.midSpanPhotoLocationIconSymbology)
            ) {
                layers.push(this.getMidSpanPhotoLocationSymbology());
            }

            if (
                !_isNil(this.props.streetViewHeading) &&
                !_isNil(this.props.streetViewPosition)
            ) {
                layers.push(
                    new IconLayer({
                        id: 'pegman-layer',
                        data: [
                            {
                                coordinates: [
                                    this.props.streetViewPosition.lng,
                                    this.props.streetViewPosition.lat,
                                ],
                            },
                        ],
                        getIcon: () => {
                            return {
                                url: pegmanIcon,
                                height: 887,
                                width: 382,
                                anchorX: 190,
                                anchorY: 800,
                            };
                        },
                        getPosition: (d) => d.coordinates,
                        sizeUnits: 'common',
                        sizeMinPixels: 5,
                        sizeMaxPixels: 25,
                    })
                );

                layers.push(
                    new GeoJsonLayer({
                        id: 'streetViewPovLayer',
                        data: createFovPolygon(
                            this.props.streetViewPosition.lat,
                            this.props.streetViewPosition.lng,
                            this.props.streetViewHeading
                        ),
                        getFillColor: [0, 0, 255, 50],
                        getLineColor: [0, 0, 255, 0],
                    })
                );
            }

            this.overlay.setProps({
                layers: layers.reverse(),
            });
        }

        return <div id={this.id} className={Styles.mapContainer} />;
    }
}
